import React, { useState, useEffect } from "react";
import { 
  FiChevronLeft, 
  FiThermometer, 
  FiDroplet, 
  FiCloudRain,
  FiWind,
  FiLayers,
  FiChevronRight
} from 'react-icons/fi';
import './LayerDataInfo.css';
import { selectLayerDataSet } from "../../../features/layers/overlaylayerslice";
import { useDispatch, useSelector } from "react-redux";
import { setAnalyticsDetails } from "../../../features/layers/overlaylayerslice";

const LayerDataInfo = () => {
  // State management
  const [showSidebar, setShowSidebar] = useState(true);
  const [pressureIndex, setPressureIndex] = useState(1); // Index-based pressure level selection
  const [opacity, setOpacity] = useState(75); // Default opacity
  const [windsOn, setWindsOn] = useState(true); // Wind vectors toggle
  const [geopotentialOn, setGeopotentialOn] = useState(false); // Geopotential heights toggle
  const [selectedModel, setSelectedModel] = useState(""); // Currently selected weather model
  const [weatherParameter, setWeatherParameter] = useState(""); // Selected weather parameter
  const [lastWindState, setLastWindState] = useState(true); // Remembers last wind state
  const [lastGeopotentialState, setLastGeopotentialState] = useState(false); // Remembers last geopotential state
  const [disableOptions, setDisableOptions] = useState(false); // Disables options when needed
  const [isMobile, setIsMobile] = useState(false); // Track if device is mobile

  const dispatch = useDispatch();
  const tasks = useSelector(selectLayerDataSet);
  
  // Standard pressure levels (hPa)
  const altitudeValues = [925, 850, 700, 500, 200];

  // Toggle sidebar visibility
  const toggleSidebar = () => setShowSidebar(!showSidebar);

  // Check if device is mobile on component mount
  useEffect(() => {
    const checkIfMobile = () => {
      const isMobileDevice = window.innerWidth <= 768; // Common mobile breakpoint
      setIsMobile(isMobileDevice);
      
      if (isMobileDevice) {
        // Hide sidebar after 2 seconds on mobile
        const timer = setTimeout(() => {
          setShowSidebar(false);
        }, 2000);
        
        return () => clearTimeout(timer);
      }
    };

    checkIfMobile();
    
    // Add resize listener to handle orientation changes
    const handleResize = () => {
      checkIfMobile();
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Filter available models and administrative layers
  const modelOptions = tasks
    .filter(task => task.class === 'Model')
    .map(task => task);

  const administrativeOptions = tasks
    .filter(task => task.class === 'Administrative')
    .map(task => task);

  // Get current selected model data
  const currentModel = modelOptions.find(model => model.text === selectedModel) || 
                      (modelOptions.length > 0 ? modelOptions[0] : null);

  // Check if selected model is WBGT
  const isWBGTSelected = currentModel?.text === "WBGT";
  
  // Check if current parameter is WBGT
  const isWBGTParameter = weatherParameter === "WBGT";

  // Initialize component with default values
  useEffect(() => {
    if (modelOptions.length > 0 && !selectedModel) {
      const firstModel = modelOptions[0];
      setSelectedModel(firstModel.text);
      
      // Initialize with first parameter if available
      if (firstModel.variables?.variables?.length > 0) {
        setWeatherParameter(firstModel.variables.variables[0]);
      }
      
      // Initialize pressure with model's altitude or default to 850hPa
      const initialAlt = firstModel.altitude || 850;
      setPressureIndex(altitudeValues.indexOf(initialAlt));
    }
  }, [modelOptions, selectedModel]);

  // Handle administrative layer toggles
  const handleAdministrativeChange = (option) => {
    dispatch(setAnalyticsDetails({ 
      id: option.id - 1,
      show: !option.show
    }));
  };

  const handleModelChange = (e) => {
    const newModelName = e.target.value;
    
    // First, find the newly selected model
    const newModel = modelOptions.find(model => model.text === newModelName);
    
    if (!newModel) return;
    
    // Set all models to false except the selected one
    modelOptions.forEach(model => {
      // Use the correct id calculation based on your Redux structure
      const idToUse = model.id - 1;
      
      dispatch(setAnalyticsDetails({
        id: idToUse,
        show: model.text === newModelName // true only for selected model
      }));
    });
    
    // Update selected model state
    setSelectedModel(newModelName);
    
    // Reset states for the new model
    setWeatherParameter("");
    setWindsOn(true);
    setGeopotentialOn(false);
    setDisableOptions(false);
    
    // Automatically select the first parameter if available
    if (newModel.variables?.variables?.length > 0) {
      const firstParam = newModel.variables.variables[0];
      setWeatherParameter(firstParam);
      
      // Set up the model with the first parameter
      const datatype = firstParam === "Accumulated Rainfall" ? 'acurain' : firstParam.toLowerCase();
      
      dispatch(setAnalyticsDetails({
        id: newModel.id - 1,
        datatype: datatype,
        layer: newModel.layer,
        showGeopotentialHeights: false,
        showWindVectors: false,
        show: true // Ensure it's shown
      }));
    } else {
      // If no parameters, just show the model
      dispatch(setAnalyticsDetails({
        id: newModel.id - 1,
        show: true
      }));
    }
  };

  // Handle weather parameter selection with proper layer management
  const handleWeatherParameterChange = (param) => {
    const newParam = weatherParameter === param ? "" : param;
    setWeatherParameter(newParam);

    if (!currentModel) return;

    if (newParam === "Accumulated Rainfall") {
      // Special handling for accumulated rainfall
      setLastWindState(windsOn);
      setLastGeopotentialState(geopotentialOn);
      setWindsOn(false);
      setGeopotentialOn(false);
      setDisableOptions(true);

      dispatch(
        setAnalyticsDetails({
          id: currentModel.id - 1,
          datatype: 'acurain',
          layer: currentModel.layer,
          showGeopotentialHeights: false,
          showWindVectors: false,
          show: true // Force show when selecting
        })
      );
    } else if (newParam === "") {
      // Deselect all parameters - hide the model
      dispatch(setAnalyticsDetails({ 
        id: currentModel.id - 1, 
        show: false 
      }));
      setDisableOptions(true);
    } else {
      // Standard parameter selection
      setDisableOptions(false);
      setWindsOn(lastWindState);
      setGeopotentialOn(lastGeopotentialState);

      dispatch(
        setAnalyticsDetails({
          id: currentModel.id - 1,
          datatype: newParam.toLowerCase(),
          layer: currentModel.layer,
          showGeopotentialHeights: geopotentialOn,
          showWindVectors: windsOn,
          show: true // Force show when selecting
        })
      );
    }
  };

  // Handle pressure level changes
  const handlePressureChange = (e) => {
    const newIndex = Number(e.target.value);
    setPressureIndex(newIndex);
    const newAltitude = altitudeValues[newIndex];
    
    if (currentModel && weatherParameter) {
      dispatch(
        setAnalyticsDetails({
          id: currentModel.id - 1,
          altitude: newAltitude,
        })
      );
    }
  };

  // Toggle wind vectors
  const handleWindToggle = () => {
    const newState = !windsOn;
    setWindsOn(newState);
    
    if (currentModel && weatherParameter && !disableOptions) {
      dispatch(
        setAnalyticsDetails({
          id: currentModel.id - 1,
          showWindVectors: newState,
        })
      );
    }
  };

  // Toggle geopotential heights
  const handleGeopotentialToggle = () => {
    const newState = !geopotentialOn;
    setGeopotentialOn(newState);
    
    if (currentModel && weatherParameter && !disableOptions) {
      dispatch(
        setAnalyticsDetails({
          id: currentModel.id - 1,
          showGeopotentialHeights: newState,
        })
      );
    }
  };

  // Handle opacity changes
  const handleOpacityChange = (e) => {
    const newValue = Number(e.target.value);
    setOpacity(newValue);
    
    if (currentModel) {
      dispatch(
        setAnalyticsDetails({
          id: currentModel.id - 1,
          opacity: newValue,
        })
      );
    }
  };

  // Get available weather parameters from current model or use defaults
  const availableWeatherParameters = currentModel?.variables?.variables || [
    "Temperature",
    "Humidity",
    "Rainfall",
    "Accumulated Rainfall",
    "Wind"
  ];

  // Icon mapping for weather parameters
  const weatherIcons = {
    "Temperature": <FiThermometer size={14} />,
    "Humidity": <FiDroplet size={14} />,
    "Rainfall": <FiCloudRain size={14} />,
    "Accumulated Rainfall": <FiCloudRain size={14} />,
    "Wind": <FiWind size={14} />,
    "WBGT": <FiThermometer size={14} /> // Add WBGT icon
  };

  // Check if we should show pressure slider
  const showPressureSlider = !isWBGTParameter && 
                            weatherParameter && 
                            weatherParameter !== "Accumulated Rainfall" && 
                            weatherParameter !== "Rain";

  return (
    <div className="layer-data-info-container">
      {/* Sidebar Content */}
      {showSidebar && (
        <div className="layer-panel responsive-panel compact">
          {/* Toggle Button */}
          <button
            onClick={toggleSidebar}
            className="toggleButton top-left fixed"
            aria-label="Hide sidebar"
          >
            <FiChevronRight />
          </button>

          {/* Administrative Layers Section */}
          <div className="form-group small-font">
            <label>Administrative:</label>
            {administrativeOptions.length > 0 ? (
              <div className="checkbox-group">
                {administrativeOptions.map(opt => (
                  <label key={opt.id} className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={opt.show}
                      onChange={() => handleAdministrativeChange(opt)}
                    /> 
                    <span className="checkmark"></span>
                    {opt.text}
                  </label>
                ))}
              </div>
            ) : (
              <p>No administrative options available</p>
            )}
          </div>
          
          {/* Model Selection */}
          <div className="form-group small-font compact-select">
            <label>Choose Model</label>
            <select 
              value={selectedModel} 
              onChange={handleModelChange}  // Updated handler
              disabled={modelOptions.length === 0}
            >
              {modelOptions.map(model => (
                <option key={model.id} value={model.text} style={{
        backgroundColor: '#2a2a3a', // Dark background
        color: 'white', // White text
      }}>
                  {model.text}
                </option>
              ))}
            </select>
          </div>

          {/* Weather Parameters Section (only shown when model is selected) */}
          {currentModel && (
            <>
              <div className="form-group small-font">
                <label>Weather Parameter:</label>
                <div className="button-grid two-line-grid">
                  {availableWeatherParameters.map(param => (
                    <button
                      key={param}
                      onClick={() => handleWeatherParameterChange(param)}
                      className={weatherParameter === param ? 'active' : ''}
                      disabled={!currentModel}
                    >
                      <span className="icon-text">
                        {weatherIcons[param] || <FiThermometer size={14} />}
                        <span className="param-text">{param}</span>
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Sliders for Pressure and Opacity */}
              <div className="slider-group compact-sliders">
                {/* Conditionally render Pressure slider */}
                {showPressureSlider && (
                  <>
                    <label className="small-font slider-label">
                      Pressure: <span className="value-text">{altitudeValues[pressureIndex]} hPa</span>
                    </label>
                    <input
                      type="range"
                      min={0}
                      max={altitudeValues.length - 1}
                      step={1}
                      value={pressureIndex}
                      onChange={handlePressureChange}
                      disabled={!weatherParameter || weatherParameter === "Accumulated Rainfall" || weatherParameter === "Rain"}
                    />
                  </>
                )}
                
                {/* Opacity slider always visible */}
                <label className="small-font slider-label">
                  Opacity: <span className="value-text">{opacity}%</span>
                </label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={opacity}
                  onChange={handleOpacityChange}
                />
              </div>

              {/* Toggle Buttons for Winds and Geopotential */}
              <div className="toggle-group small-font">
                <div className="toggle-item">
                  <span><FiWind size={14} /> Winds</span>
                  <button 
                    onClick={handleWindToggle} 
                    className={windsOn ? 'on' : 'off'}
                    disabled={disableOptions || !weatherParameter || weatherParameter === "Accumulated Rainfall" || isWBGTParameter}
                  >
                    {windsOn ? 'ON' : 'OFF'}
                  </button>
                </div>
                <div className="toggle-item">
                  <span><FiLayers size={14} /> Geopotential</span>
                  <button 
                    onClick={handleGeopotentialToggle} 
                    className={geopotentialOn ? 'on' : 'off'}
                    disabled={disableOptions || !weatherParameter || weatherParameter === "Accumulated Rainfall" || isWBGTParameter}
                  >
                    {geopotentialOn ? 'ON' : 'OFF'}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Collapsed Sidebar Toggle Button */}
      {!showSidebar && (
        <button
          onClick={toggleSidebar}
          className="toggleButton top-left collapsed fixed"
          aria-label="Show sidebar"
        >
          <FiChevronRight />
        </button>
      )}
    </div>
  );
};

export default LayerDataInfo;